import { useRef, useState } from 'react'
import { Loader2, Trash2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { resizeImageToDataUrl } from '@/lib/imageUpload'

export type ThemeMode = '' | 'system' | 'light' | 'dark'

const HEX = /^#[0-9a-fA-F]{6}$/
const MAX_ICON_FILE_BYTES = 5 * 1024 * 1024

/** The logo in the storefront header: upload, paste a link, or fall back to the receipt logo. */
export function LogoField({
  logo,
  placeholder,
  onChange,
}: {
  logo: string
  placeholder: string
  onChange: (v: string) => void
}) {
  const { toast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const handle = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Not an image', description: 'Choose a PNG, JPEG, WebP or SVG file.', variant: 'destructive' })
      return
    }
    if (file.size > MAX_ICON_FILE_BYTES) {
      toast({ title: 'Image too large', description: 'Choose a file under 5 MB.', variant: 'destructive' })
      return
    }
    setBusy(true)
    try {
      onChange(await resizeImageToDataUrl(file, 480, 0.9))
    } catch (err) {
      toast({ title: 'Could not process image', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="sf-logo">Logo</Label>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
      />
      <div className="flex items-center gap-3">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted">
          {logo || placeholder ? <img src={logo || placeholder} alt="" className="h-full w-full object-contain" /> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {logo ? 'Replace' : 'Upload logo'}
          </Button>
          {logo && (
            <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => onChange('')}>
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          )}
        </div>
      </div>
      <Input
        id="sf-logo"
        type="url"
        inputMode="url"
        placeholder="Or paste a link: https://…/logo.png"
        value={logo.startsWith('data:') ? '' : logo}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-[11px] text-muted-foreground">Shown in the storefront header. Blank uses your receipt logo.</p>
    </div>
  )
}

interface Props {
  pwaShortName: string
  appIconUrl: string
  onPwaShortName: (v: string) => void
  onAppIconUrl: (v: string) => void
}

/** What the site looks like once a customer adds it to their phone's home screen. */
export function IdentityCard(p: Props) {
  const { toast } = useToast()
  const iconRef = useRef<HTMLInputElement>(null)
  const [iconBusy, setIconBusy] = useState(false)

  const handleIcon = async (file: File | undefined) => {
    if (iconRef.current) iconRef.current.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Not an image', description: 'Choose a PNG, JPEG or WebP file.', variant: 'destructive' })
      return
    }
    if (file.size > MAX_ICON_FILE_BYTES) {
      toast({ title: 'Image too large', description: 'Choose a file under 5 MB.', variant: 'destructive' })
      return
    }
    setIconBusy(true)
    try {
      p.onAppIconUrl(await resizeImageToDataUrl(file, 512, 0.9))
    } catch (err) {
      toast({ title: 'Could not process image', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setIconBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Home-screen icon</Label>
        <input
          ref={iconRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => handleIcon(e.target.files?.[0])}
        />
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border bg-muted">
            {p.appIconUrl ? <img src={p.appIconUrl} alt="" className="h-full w-full object-cover" /> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={iconBusy} onClick={() => iconRef.current?.click()}>
              {iconBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {p.appIconUrl ? 'Replace' : 'Upload icon'}
            </Button>
            {p.appIconUrl && (
              <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => p.onAppIconUrl('')}>
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">A square image, at least 512 px. Blank uses your logo.</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="sf-pwa-name">Home-screen name</Label>
          <span className="text-[11px] tabular-nums text-muted-foreground">{p.pwaShortName.length}/12</span>
        </div>
        <Input
          id="sf-pwa-name"
          maxLength={12}
          placeholder="Your café"
          value={p.pwaShortName}
          onChange={(e) => p.onPwaShortName(e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground">
          The label under the icon. Blank uses the first 12 letters of your restaurant name.
        </p>
      </div>
    </div>
  )
}

export function ColorField({
  label,
  hint,
  value,
  placeholder,
  onChange,
  invalid,
}: {
  label: string
  hint: string
  value: string
  placeholder: string
  onChange: (v: string) => void
  invalid: boolean
}) {
  const swatch = HEX.test(value) ? value : HEX.test(placeholder) ? placeholder : '#16a34a'
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} picker`}
          value={swatch}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1"
        />
        <Input
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={invalid ? 'border-destructive focus-visible:ring-destructive' : 'font-mono'}
        />
      </div>
      <p className={`text-[11px] ${invalid ? 'text-destructive' : 'text-muted-foreground'}`}>
        {invalid ? 'Enter a 6-digit hex colour like #16a34a.' : hint}
      </p>
    </div>
  )
}
